import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getProject,
  getProjectFeatures,
  getProjectGallery,
  getProjectActions,
} from '../../../../api/portfolio';

function ArrowIcon() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 9 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.71108 3.78684L8.22361 4.29813L7.71263 4.80992L4.64672 7.87832L4.13433 7.36688L6.87531 4.62335H1.11181H0.750039H0.388177L0.382812 0.718232H1.10645L1.11082 3.90005H6.80113L4.12591 1.22972L4.63689 0.718262L7.71108 3.78684Z"
        fill="#fff"
      />
    </svg>
  );
}

function ProjectView() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [features, setFeatures] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getProject(id),
      getProjectFeatures(id),
      getProjectGallery(id),
      getProjectActions(id),
    ])
      .then(([p, f, g, a]) => {
        setProject(p);
        setFeatures(f);
        setGallery(g);
        setActions(a);
      })
      .catch((err) => setError(err.message || 'Failed to load project'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <section className="sec-box project section-padding radius-15">
        <p className="text-center opacity-7">Loading project...</p>
      </section>
    );
  }

  if (error || !project) {
    return (
      <section className="sec-box project section-padding radius-15">
        <p className="text-center opacity-7">
          {error || 'Project not found.'}
        </p>
      </section>
    );
  }

  return (
    <section className="sec-box project section-padding radius-15">
      <div className="row justify-content-center">
        <div className="col-lg-11">
          {project['Project Cover'] && (
            <div className="img mb-80">
              <img
                src={project['Project Cover']}
                alt={project['Project Title'] || ''}
                className="radius-5"
              />
            </div>
          )}
          <div className="row justify-content-center">
            <div className="col-lg-7">
              <div className="cont md-mb50">
                <h3 className="mb-15 fw-500">{project['Project Title']}</h3>
                {project['Project Description'] && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: project['Project Description'],
                    }}
                  />
                )}
                {project['Project Insight'] && (
                  <div
                    className="mt-30 line-height-28"
                    dangerouslySetInnerHTML={{
                      __html: project['Project Insight'],
                    }}
                  />
                )}
                {features.length > 0 && (
                  <ul className="rest list-arrow mt-50">
                    {features.map((f, idx) => (
                      <li key={f.id ?? idx} className={idx === 0 ? '' : 'mt-20'}>
                        <span className="icon">
                          <ArrowIcon />
                        </span>
                        <h6 className="inline fz-18">{f['Feature Title']}</h6>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="col-lg-4">
              <div className="info sub-bg" id="sticky_item">
                <ul>
                  {project['Project Date'] && (
                    <li className="mb-30">
                      <span className="sub-title">
                        <i className="far fa-calendar-alt mr-10"></i> Date :
                      </span>
                      <p>{project['Project Date']}</p>
                    </li>
                  )}
                  {project['Project Website'] && (
                    <li>
                      <span className="sub-title">
                        <i className="fas fa-globe mr-10"></i> Website :
                      </span>
                      <p>
                        <a
                          href={project['Project Website']}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {project['Project Website']}
                        </a>
                      </p>
                    </li>
                  )}
                </ul>
              </div>
              {actions.length > 0 && (
                <div className="flex flex-wrap gap-4 mt-4">
                  {actions.map((a, idx) => (
                    <a
                      key={a.id ?? idx}
                      href={a.Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn2 w-[200px] bg-[#91150c] border border-[#92140C] py-[3px] rounded-[6px] no-underline text-white text-center text-base transition-all duration-300 transform hover:bg-[#1e1e24] hover:text-[#fcf5ed] hover:scale-105 flex items-center justify-center gap-2"
                    >
                      {a.Label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {gallery.length > 0 && (
        <div className="imgs mt-80">
          <div className="row justify-content-center">
            <div className="col-lg-11">
              <div className="row md-marg">
                {gallery.map((img, idx) => (
                  <div key={img.id ?? idx} className="col-md-6">
                    <div className="img sm-mb30">
                      <img src={img.Image} alt={img['Image Tag'] || ''} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ProjectView;
